"use client";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCircleCheckOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";

import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { V2_ENDPOINT } from "@/consts";
import { useDebounce } from "@/hooks/useDebounce";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";

// Twitter handle rules: 1–15 chars, alphanumeric + underscore. Mirrors the
// trigger schema's pattern at services/b3os-workflow/configs/json/triggers.json
// so client-side validation rejects the same strings the backend would.
const USERNAME_RE = /^[A-Za-z0-9_]{1,15}$/;

interface TwitterUserResult {
  username: string;
  name: string;
  profilePicture: string | null;
  isBlueVerified: boolean;
  followers: number;
  description: string | null;
}

function formatFollowers(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

async function searchTwitterUsers(query: string): Promise<TwitterUserResult[]> {
  const res = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/x-search-users/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ inputs: { query } }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  const result = data?.data?.result || data?.result || data?.data;
  return result?.users ?? [];
}

async function lookupTwitterUser(username: string): Promise<TwitterUserResult | null> {
  const res = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/x-get-user-info/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ inputs: { username } }),
  });
  if (!res.ok) return null;
  const data = await res.json();
  const result = data?.data?.result || data?.result || data?.data;
  if (!result?.username) return null;
  return {
    username: result.username,
    name: result.name ?? result.username,
    profilePicture: result.profilePicture ?? null,
    isBlueVerified: result.isBlueVerified ?? false,
    followers: result.followers ?? 0,
    description: result.description ?? null,
  };
}

interface TwitterUserSelectorProps {
  label: string;
  // Accepts both shapes for backward compatibility with single-string templates
  // and the array-username trigger payload. Internally coerced to an array.
  value: string | string[] | undefined;
  // Emits an array (or undefined for empty) so consumers have a single shape
  // to handle. Callers that need to store a single string for backward compat
  // can take `v?.[0]` when `v.length === 1`.
  onChange: (value: string[] | undefined) => void;
  required?: boolean;
}

// Single chip-list UI that handles 0, 1, or many usernames. We dropped the
// legacy single-pill mode because the schema (oneOf [string, array]) and the
// trigger semantics both treat 1-user and N-user as the same operation — the
// only difference is the wire format, which the caller resolves.
export default function TwitterUserSelector({ label, value, onChange, required }: TwitterUserSelectorProps) {
  const usernames: string[] = Array.isArray(value) ? value : typeof value === "string" && value ? [value] : [];
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 500);

  const { data: results = [], isLoading } = useQuery({
    queryKey: ["twitterUserSearch", debouncedQuery],
    queryFn: () => searchTwitterUsers(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  // Look up metadata for every chip in parallel. TanStack dedupes by query key
  // so re-renders don't refetch. `combine` returns a plain object so React
  // Query's structural sharing keeps the ref stable when results don't change.
  const { byUsername } = useQueries({
    queries: usernames.map(username => ({
      queryKey: ["twitterUserLookup", username],
      queryFn: () => lookupTwitterUser(username),
      staleTime: 5 * 60_000,
    })),
    combine: results => {
      const byUsername: Record<string, TwitterUserResult | null> = {};
      results.forEach((r, i) => {
        byUsername[usernames[i]] = r.data ?? null;
      });
      return { byUsername };
    },
  });

  const appendUsername = (username: string) => {
    if (!USERNAME_RE.test(username)) return;
    // Twitter handles are case-insensitive — VitalikButerin and vitalikbuterin are the same account.
    const lowered = username.toLowerCase();
    if (usernames.some(u => u.toLowerCase() === lowered)) return;
    onChange([...usernames, username]);
  };

  const removeUsername = (username: string) => {
    const next = usernames.filter(u => u !== username);
    // Emit undefined when the list empties so the field becomes "absent" — the
    // oneOf schema's `minItems: 1` would otherwise reject the empty array at
    // publish time.
    onChange(next.length === 0 ? undefined : next);
  };

  const handleSelect = (user: TwitterUserResult) => {
    appendUsername(user.username);
    setQuery("");
    searchInputRef.current?.focus({ preventScroll: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = query.trim().replace(/^@/, "");
    if (!trimmed) return;
    appendUsername(trimmed);
    setQuery("");
  };

  const showDropdown = isOpen && (debouncedQuery.length >= 2 || results.length > 0);

  return (
    <div className="space-y-2" ref={setContainerEl}>
      {label && (
        <label className="field-label block">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {usernames.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {usernames.map(username => {
            const meta = byUsername[username];
            return (
              <div
                key={username}
                className="border-ds-border-default bg-ds-surface-card flex items-center gap-1.5 rounded-full border py-0.5 pl-1 pr-2 text-sm"
              >
                {meta?.profilePicture ? (
                  <img src={meta.profilePicture} alt="" className="h-5 w-5 rounded-full object-cover" />
                ) : (
                  <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-medium">
                    {(meta?.name?.[0] ?? username[0])?.toUpperCase()}
                  </div>
                )}
                <span className="text-ds-text-primary">
                  @{username}
                  {meta?.isBlueVerified && <IconCircleCheckOutline18 className="ml-0.5 inline h-3 w-3 text-blue-500" />}
                </span>
                <button
                  type="button"
                  onClick={() => removeUsername(username)}
                  className="hover:bg-ds-neutral-200 dark:hover:bg-ds-neutral-700 rounded-full p-0.5"
                  aria-label={`Remove ${username}`}
                >
                  <IconXmarkOutline18 className="text-ds-text-disabled h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Popover
        open={showDropdown}
        onOpenChange={next => {
          if (!next) setIsOpen(false);
        }}
        modal={false}
      >
        <PopoverTrigger asChild>
          <div className="relative">
            <IconMagnifyingGlass className="text-ds-text-disabled absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => {
                if (query.length >= 2) setIsOpen(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search or type @username and press Enter"
              className="text-ds-text-primary py-2 pl-9 pr-3"
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
          sideOffset={4}
          container={containerEl ?? undefined}
          onOpenAutoFocus={e => {
            e.preventDefault();
            searchInputRef.current?.focus({ preventScroll: true });
          }}
        >
          <SearchDropdownItems
            isLoading={isLoading}
            results={results}
            debouncedQuery={debouncedQuery}
            onPick={handleSelect}
            disabledUsernames={usernames}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Shared search dropdown render
// ──────────────────────────────────────────────────────────────────────────

interface SearchDropdownItemsProps {
  isLoading: boolean;
  results: TwitterUserResult[];
  debouncedQuery: string;
  onPick: (user: TwitterUserResult) => void;
  // Usernames to render with a "selected" affordance — multi mode passes the
  // current chip list here so users see which suggestions are already added.
  disabledUsernames: string[] | undefined;
}

function SearchDropdownItems({
  isLoading,
  results,
  debouncedQuery,
  onPick,
  disabledUsernames,
}: SearchDropdownItemsProps) {
  // Case-insensitive lookup — Twitter handles equate VitalikButerin and vitalikbuterin.
  const disabledLowered = new Set(disabledUsernames?.map(u => u.toLowerCase()));
  return (
    <div className="max-h-60 overflow-auto">
      {isLoading && <div className="text-ds-text-muted px-3 py-2 text-sm">Searching...</div>}
      {!isLoading && results.length === 0 && debouncedQuery.length >= 2 && (
        <div className="text-ds-text-muted px-3 py-2 text-sm">No users found</div>
      )}
      {results.map(user => {
        const isSelected = disabledLowered.has(user.username.toLowerCase());
        return (
          <button
            key={user.username}
            type="button"
            onClick={() => onPick(user)}
            disabled={isSelected}
            className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 flex w-full items-center gap-3 px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {user.profilePicture ? (
              <img src={user.profilePicture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium">
                {user.name[0]?.toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-ds-text-primary flex items-center gap-1 truncate text-sm font-medium">
                {user.name}
                {user.isBlueVerified && (
                  <IconCircleCheckOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                )}
              </div>
              <div className="text-ds-text-muted truncate text-xs">
                @{user.username} · {formatFollowers(user.followers)} followers
              </div>
            </div>
            {isSelected && <span className="text-ds-text-muted text-xs">Added</span>}
          </button>
        );
      })}
    </div>
  );
}
